import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import api from "../api/client";

const MetaCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setFirebaseInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!firebaseInitialized) return;

    if (!user) {
      setErrorMessage("You're not logged in. Please log in before connecting social media.");
      return;
    }

    const handleMetaCallback = async () => {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get("code");
      const stateRaw = urlParams.get("state");

      if (!code || !stateRaw) {
        setErrorMessage("Missing authorization code or state from Meta.");
        return;
      }

      let provider: string | undefined;
      try {
        const decodedState = decodeURIComponent(stateRaw);
        provider = JSON.parse(decodedState).provider;
      } catch {
        setErrorMessage("Invalid callback state. Please try again.");
        return;
      }

      try {
        await user.getIdToken(true);

        await api.post("/meta/oauth/callback", { code, provider, state: stateRaw });

        navigate(`/settings?success=${provider}_connected`);
      } catch (err: any) {
        console.error("❌ MetaCallback error:", err);
        const status = err.response?.status;
        const msg = err.response?.data?.error;

        if (status === 409) {
          setErrorMessage(msg || `This ${provider} account is already connected to another business.`);
        } else {
          setErrorMessage(msg || `Failed to connect ${provider}. Please try again.`);
        }
      }
    };

    handleMetaCallback();
  }, [firebaseInitialized, user, navigate, location]);

  return (
    <Box sx={{ p: 4, maxWidth: 600, margin: "auto", textAlign: "center" }}>
      {errorMessage ? (
        <>
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            You may go back to <a href="/settings">Settings</a> to try again.
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Finishing up your connection...</Typography>
        </>
      )}
    </Box>
  );
};

export default MetaCallback;
